import React, { Fragment, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import 'react-vertical-timeline-component/style.min.css';
import { makeStyles, withTheme } from '@material-ui/core/styles';
import _orderBy from 'lodash/orderBy';
import { Grid, Typography } from '@material-ui/core';

import EmailIcon from '@material-ui/icons/Email';

import CutIcon from '../../Components/CustomIcons/CutIcon';
import CertificateIcon from '../../Components/CustomIcons/CertificateIcon';
import PendingIcon from '../../Components/CustomIcons/PendingIcon';
import CheckIcon from '../../Components/CustomIcons/CheckBigIcon';

import BaseButton from '../../Components/Buttons/BaseButton';

import Timeline from '../../Components/Timeline/Timeline';
import TimeLineElement from '../../Components/Timeline/TimelineElement';

import { EXEMPTION_TEXT } from '../../Utils/enums';
import { Company } from '../../Configs/general';

const FORMAT = 'MMMM DD/YYYY';

const renderExpiration = node => {
  return (
    <TimeLineElement
      key="expiration_node"
      title="Vencimiento de la exención"
      subtitle={`${moment(node.date).format(FORMAT)}`}
      icon={CutIcon}
      color={node.color}
    />
  );
};

const renderNotification = node => {
  return (
    <TimeLineElement
      key="notification_node"
      title="Notificación"
      subtitle={`${moment(node.date).format(FORMAT)}`}
      icon={EmailIcon}
      color={node.color}
    />
  );
};

const renderExemption = node => {
  const { id, initialDate, color } = node;
  return (
    <TimeLineElement
      key={`exemption_${id}_node`}
      title="Última exención"
      subtitle={`${moment(initialDate).format(FORMAT)}`}
      icon={CertificateIcon}
      color={color}
    />
  );
};

const ExemptionHistory = props => {
  const { theme, exemptionData, requestExemption, loading } = props;
  // * HOOKS
  const classes = useStyles();

  // *
  const ColombiaTime = moment()
    .tz('America/Bogota')
    .format();

  const renderCardText = useCallback(
    (text, buttonText, disableRequestButton) => {
      return (
        <Fragment>
          {text && <Typography className={classes.itemText}>{text}</Typography>}
          {buttonText && !disableRequestButton && (
            <Grid container justifyContent="flex-end">
              <BaseButton
                className={classes.itemButton}
                variant="outlined"
                size="small"
                onClick={requestExemption}
                loading={loading}
                progress={loading}
              >
                {buttonText}
              </BaseButton>
            </Grid>
          )}
        </Fragment>
      );
    },
    [classes, loading, requestExemption]
  );

  const renderCurrentState = useCallback(
    node => {
      // Exemption effective still
      const expiresIn = moment(node.data.finalDate).fromNow();
      const month = moment().diff(moment(node.data.initialDate), 'months') + 1;

      let nodeTitle = 'Exención vigente';
      let nodeChild = (
        <span>
          Tu exención vence{' '}
          <span className={classes.textBold}>{expiresIn}</span>.
        </span>
      );

      let nodeSubtitle = `Hoy - Mes ${month}`;
      let nodeIcon = CheckIcon;

      // Exemption is soon to be expired
      if (node.data.isNotificationPeriod) {
        nodeChild = (
          <span>
            Tu exención vence{' '}
            <span className={classes.textBold}>{expiresIn}</span>.{' '}
            {EXEMPTION_TEXT.warningPeriod}
          </span>
        );
        nodeIcon = PendingIcon;
      }

      if (!node.data.isInTime) {
        nodeTitle = 'EXENCIÓN VENCIDA';
        nodeSubtitle = 'Hoy';
        nodeChild = (
          <span>
            La exención de tu{' '}
            {Company.contractConjugation.regular.singular.main}{' '}
            <span className={classes.textBold}>no se encuentra vigente</span>
            .
            <br />
            {EXEMPTION_TEXT.warningPeriod}
          </span>
        );
        nodeIcon = CutIcon;
      }

      return (
        <TimeLineElement
          key={'current_node'}
          title={nodeTitle}
          subtitle={nodeSubtitle}
          icon={nodeIcon}
          color={node.color}
        >
          {renderCardText(
            nodeChild,
            node.data.isNotificationPeriod ? 'Solicitar Exención' : '',
            !node.data.isNotificationPeriod
          )}
        </TimeLineElement>
      );
    },
    [classes, renderCardText]
  );

  const contractExemptionHistory = useMemo(() => {
    const isCancelled = Boolean(exemptionData.cancelledAt);
    const finalDate = moment(exemptionData.finalDate);
    //Note: Moment.subtract() **mutates** the object, so we have to create a copy of finalDate
    const notificationDate = moment(exemptionData.finalDate).subtract(
      1,
      'month'
    );

    const exemptionHistory = [];

    // Set the color of the nodes depending on the period
    let nodesColor = theme.palette.color.whiteDark;

    const now = moment();

    const isInTime = now.isBefore(finalDate) && !isCancelled;
    const isNotificationPeriod = now.isAfter(notificationDate);
    if (isInTime) {
      nodesColor = theme.palette.success.main;
    }

    if (isNotificationPeriod) {
      nodesColor = theme.palette.warning.main;
    }

    if (!isInTime) {
      nodesColor = theme.palette.error.main;
    }

    // Add deadline nodes that build the history
    if (isInTime) {
      exemptionHistory.push({
        type: 'expiration',
        date: finalDate,
        node: renderExpiration({
          date: finalDate,
          color: isNotificationPeriod
            ? theme.palette.error.main
            : theme.palette.color.paleGrey
        })
      });

      exemptionHistory.push({
        type: 'notification',
        date: notificationDate,
        node: renderNotification({
          date: notificationDate,
          color: isNotificationPeriod
            ? theme.palette.warning.main
            : theme.palette.color.paleGrey
        })
      });
    } else {
      exemptionHistory.push({
        type: 'notification',
        date: notificationDate,
        node: renderNotification({
          date: notificationDate,
          color: theme.palette.error.main
        })
      });
    }

    // Add the current state node
    exemptionHistory.push({
      type: 'currently',
      date: ColombiaTime,
      node: renderCurrentState({
        data: { ...exemptionData, isNotificationPeriod, isInTime },
        color: nodesColor
      })
    });

    if (!isCancelled) {
      exemptionHistory.push({
        type: 'exemption',
        date: exemptionData.initialDate,
        node: renderExemption({
          ...exemptionData,
          color: nodesColor
        })
      });
    }

    const nodesInOrder = _orderBy(exemptionHistory, ['date'], 'desc');

    return nodesInOrder;
  }, [exemptionData, ColombiaTime, theme, renderCurrentState]);

  // * RENDER
  return (
    <Fragment>
      <Timeline withLine>{contractExemptionHistory.map(h => h.node)}</Timeline>
    </Fragment>
  );
};

const useStyles = makeStyles(theme => ({
  itemText: {
    fontSize: '14px !important',
    marginTop: theme.spacing(1.5),
    fontWeight: '400 !important'
  },
  itemButton: {
    fontSize: '14px !important',
    marginTop: 14
  },
  buttonWarning: {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.light
    }
  },
  buttonError: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.lightest
    }
  },
  timeLineFuture: {
    paddingBottom: 0
  },
  textBold: {
    fontWeight: 500
  }
}));

export default withTheme(ExemptionHistory);
